import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  userAddressesSelector,
  userPhoneNumbersSelector,
  userEmergencyContactsSelector,
  userHomeBasesSelector,
} from "Bands/state";
import {
  IUserEmergencyContact,
  IUserHomeBase,
  USER_ACTIONS,
} from "Bands/types";
import { IPhoneNumber, IPlace } from "shared/types";
import { usePagination } from "./usePagination";

export const useSortedContactDetails = () => {
  const { pages: addressPages } = usePagination(USER_ACTIONS.getUserAddresses);
  const addressIds = useMemo(() => addressPages?.[1], [addressPages]);

  const { pages: phonePages } = usePagination(USER_ACTIONS.getUserPhoneNumbers);
  const phoneIds = useMemo(() => phonePages?.[1], [phonePages]);

  const { pages: contactPages } = usePagination(
    USER_ACTIONS.getUserEmergencyContacts
  );
  const contactIds = useMemo(() => contactPages?.[1], [contactPages]);

  const { pages: basePages } = usePagination(USER_ACTIONS.getUserHomeBases);
  const baseIds = useMemo(() => basePages?.[1], [basePages]);

  const unsortedAddresses = useSelector(userAddressesSelector);
  const usortedPhoneNumbers = useSelector(userPhoneNumbersSelector);
  const unsortedEmergencyContacts = useSelector(userEmergencyContactsSelector);
  const unsortedHomeBases = useSelector(userHomeBasesSelector);

  const addresses = useMemo(
    () =>
      addressIds
        ?.map((path) => unsortedAddresses?.find(({ id }) => path.includes(id)))
        .filter((item) => item !== undefined) as IPlace[],
    [addressIds, unsortedAddresses]
  );

  const phoneNumbers = useMemo(
    () =>
      phoneIds
        ?.map((path) =>
          usortedPhoneNumbers?.find(({ id }) => path.includes(id))
        )
        .filter((item) => item !== undefined) as IPhoneNumber[],
    [phoneIds, usortedPhoneNumbers]
  );

  const emergencyContacts = useMemo(
    () =>
      contactIds
        ?.map((path) =>
          unsortedEmergencyContacts?.find(({ id }) => path.includes(id))
        )
        .filter((item) => item !== undefined) as IUserEmergencyContact[],
    [contactIds, unsortedEmergencyContacts]
  );

  const homeBases = useMemo(
    () =>
      baseIds
        ?.map((path) => unsortedHomeBases?.find(({ id }) => path.includes(id)))
        .filter((item) => item !== undefined) as IUserHomeBase[],
    [baseIds, unsortedHomeBases]
  );

  return { addresses, phoneNumbers, emergencyContacts, homeBases };
};
